// hero-tabs accessibility: rely on server-rendered role/id attributes and
// update selection/visibility and provide keyboard navigation.

(function() {
  "use strict";

  /**
   * Fade to a new background image on a .hero-tabs__content-wrapper element.
   *
   * Strategy: "old fades OUT, new already underneath"
   * 1. Set the new image on the wrapper background-image IMMEDIATELY.
   * 2. Place the OLD image on an overlay div at opacity:1, hiding the swap.
   * 3. Fade the overlay FROM 1 to 0 to reveal the new background.
   *
   * This ensures exactly ONE full-opacity image is visible at all times -
   * the dark ::after/::before masks stay consistent throughout, no flash.
   *
   * @param {HTMLElement} wrapper - the .hero-tabs__content-wrapper element
   * @param {string}      newUrl  - URL of the new background image
   */
  function fadeSlideBackground(wrapper, newUrl) {
    const newBgValue = "url(" + newUrl + ")";
    const currentBg = wrapper.style.backgroundImage || "";

    if (currentBg === newBgValue) return;

    let overlay = wrapper.querySelector(".hero-tabs__bg-overlay");

    if (!overlay) {
      overlay = document.createElement("div");
      overlay.className = "hero-tabs__bg-overlay";
      wrapper.insertBefore(overlay, wrapper.firstChild);
    }

    overlay.removeEventListener("transitionend", overlay._fadeEndHandler);

    overlay.style.transition = "none";
    overlay.style.backgroundImage = currentBg || "";
    overlay.style.opacity = "1";

    // eslint-disable-next-line no-unused-expressions
    overlay.offsetHeight;

    wrapper.style.backgroundImage = newBgValue;

    overlay.style.transition = "opacity 0.6s ease";
    overlay.style.opacity = "0";

    overlay._fadeEndHandler = () => {
      overlay.removeEventListener("transitionend", overlay._fadeEndHandler);
      overlay.style.backgroundImage = "";
    };

    overlay.addEventListener("transitionend", overlay._fadeEndHandler);
  }

  function productsTabSimpleSwitch() {
    const groups = document.querySelectorAll(".hero-tabs__content-products");

    if (!groups.length) return;

    groups.forEach(labels => {
      const items = Array.from(labels.querySelectorAll("li"));

      if (!items.length) return;

      const row = labels.closest(".row");
      const wrapper = row
        ? row.querySelector(".hero-tabs__content-inner-left")
        : null;

      if (!wrapper) return;

      const slideWrapper = labels.closest(".hero-tabs__content-wrapper");
      const panels = Array.from(
        wrapper.querySelectorAll(".hero-tabs__content-inner-left-product")
      );
      const activate = index => {
        items.forEach((li, i) => {
          const btn =
            li.querySelector("button[role='tab']") ||
            li.querySelector("button") ||
            li;
          const isActive = i === index;

          li.classList.toggle("is-active", isActive);
          btn.setAttribute("aria-selected", isActive ? "true" : "false");
          btn.setAttribute("tabindex", isActive ? "0" : "-1");
        });

        panels.forEach((panel, i) => {
          const isActive = i === index;

          panel.classList.toggle("is-active", isActive);

          if (isActive) {
            panel.removeAttribute("hidden");
            panel.setAttribute("aria-hidden", "false");
          } else {
            panel.setAttribute("hidden", "");
            panel.setAttribute("aria-hidden", "true");
          }
        });

        if (slideWrapper) {
          const bgDataStr = slideWrapper.dataset.slideBackgrounds;

          if (bgDataStr) {
            try {
              const bgMap = JSON.parse(bgDataStr);

              if (bgMap[index] !== undefined) {
                fadeSlideBackground(slideWrapper, bgMap[index]);
              } else {
                const defaultBg = slideWrapper.dataset.defaultBg;

                if (defaultBg) {
                  fadeSlideBackground(slideWrapper, defaultBg);
                }
              }
            } catch (e) {
              // JSON parse error - silently ignore
            }
          }
        }
      };

      let initial = items.findIndex(
        li =>
          li.classList.contains("is-active") || li.classList.contains("active")
      );

      if (initial < 0) initial = 0;

      if (slideWrapper && !slideWrapper.dataset.defaultBg) {
        const inlineStyle = slideWrapper.getAttribute("style") || "";
        const match = inlineStyle.match(
          /background-image\s*:\s*url\(['"]?([^'")\s]+)['"]?\)/
        );

        if (match && match[1]) {
          slideWrapper.dataset.defaultBg = match[1];
        }
      }

      activate(initial);

      items.forEach((li, idx) => {
        const target =
          li.querySelector("button[role='tab']") ||
          li.querySelector("button") ||
          li;

        target.addEventListener("click", e => {
          e.preventDefault();
          if (li.classList.contains("is-active")) return;
          activate(idx);
        });

        target.addEventListener("keydown", e => {
          const key = e.key;
          let newIndex = null;

          if (key === "ArrowRight" || key === "ArrowDown") {
            e.preventDefault();
            newIndex = (idx + 1) % items.length;
          } else if (key === "ArrowLeft" || key === "ArrowUp") {
            e.preventDefault();
            newIndex = (idx - 1 + items.length) % items.length;
          } else if (key === "Home") {
            e.preventDefault();
            newIndex = 0;
          } else if (key === "End") {
            e.preventDefault();
            newIndex = items.length - 1;
          } else if (key === "Enter" || key === " ") {
            e.preventDefault();
            activate(idx);

            return;
          }

          if (newIndex !== null) {
            const nextLi = items[newIndex];
            const nextTarget =
              nextLi.querySelector("button[role='tab']") ||
              nextLi.querySelector("button") ||
              nextLi;

            if (nextTarget) {
              items.forEach((otherLi, j) => {
                const otherTarget =
                  otherLi.querySelector("button[role='tab']") ||
                  otherLi.querySelector("button") ||
                  otherLi;

                otherTarget.setAttribute(
                  "tabindex",
                  j === newIndex ? "0" : "-1"
                );
              });
              nextTarget.focus();
              activate(newIndex);
            }
          }
        });
      });
    });
  }

  function heroTabsSlider() {
    const sliders = document.querySelectorAll(".hero-tabs .splide");

    if (!sliders.length) return;

    sliders.forEach(el => {
      if (el.dataset.splideInitialized === "true") return;

      const options = {
        type: "fade",
        autoplay: true,
        interval: 5000,
        perPage: 1,
        perMove: 1,
        gap: 0,
        pagination: false,
        arrows: false,
        speed: 500,
        keyboard: true,
        pauseOnHover: true,
        pauseOnFocus: true,
        rewind: true
      };

      try {
        const splide = new Splide(el, options);
        const section = el.closest(".hero-tabs");
        const labels = section
          ? Array.from(section.querySelectorAll(".hero-tabs__tabs-labels li"))
          : [];
        const updateActiveLabel = index => {
          if (!labels.length) return;
          labels.forEach((li, i) => {
            const btn = li.querySelector("button") || li;
            const isActive = i === index;

            li.classList.toggle("is-active", isActive);
            btn.setAttribute("aria-selected", isActive ? "true" : "false");
            btn.setAttribute("tabindex", isActive ? "0" : "-1");
          });
        };

        if (labels.length) {
          labels.forEach((li, idx) => {
            const target = li.querySelector("button") || li;

            target.addEventListener("click", e => {
              e.preventDefault();
              splide.go(idx);
            });
          });

          splide.on("mounted move moved", () =>
            updateActiveLabel(splide.index)
          );
        }

        splide.mount();
        el.dataset.splideInitialized = "true";
      } catch (e) {
        console.error("[hero-tabs] Failed to initialize Splide:", e);
      }
    });
  }

  document.addEventListener("DOMContentLoaded", function() {
    heroTabsSlider();
    productsTabSimpleSwitch();
  });
})();
